import React, { useCallback, useMemo } from "react";
import FloatingInput from "../../../components/input/FloatingInput";

/**
 * ParcelTab Component
 * ==================
 *
 * Allows users to search for properties by parcel number and county.
 * Validates inputs and triggers parcel search when both fields are filled.
 *
 * Flow:
 * 1. User enters parcel number
 * 2. User selects county from autocomplete
 * 3. Search button becomes available when both fields are valid
 * 4. Clicking search calls handleSearchParcel with parcel data
 * 5. Results are handled by parent component (modal display)
 *
 * @param {Object} parcelCountyAutocomplete - County autocomplete hook state/methods
 * @param {Object} parcelState - Parcel search state (number, loading status)
 * @param {Function} updateParcelNumber - Updates parcel number in state
 * @param {Function} handleSearchParcel - Triggers parcel search API call
 */
const ParcelTab = React.memo(({
  parcelCountyAutocomplete,
  parcelState,
  updateParcelNumber,
  handleSearchParcel,
}) => {
  const handleParcelNumberChange = useCallback((e) => {
    updateParcelNumber(e.target.value);
  }, [updateParcelNumber]);

  const handleParcelSearch = useCallback(() => {
   //  console.log('🔍 [ParcelTab] Parcel search initiated');
   // console.log('📝 [ParcelTab] Parcel number:', parcelState.parcelNumber);
   // console.log('📍 [ParcelTab] Selected county:', parcelCountyAutocomplete.selectedCounty);

    handleSearchParcel(parcelState.parcelNumber, parcelCountyAutocomplete.selectedCounty);
   // console.log('✅ [ParcelTab] Parcel search handler called');
  }, [handleSearchParcel, parcelState.parcelNumber, parcelCountyAutocomplete.selectedCounty]);

  // Check if form has valid data to show submit button
  const shouldShowSubmitButton = useMemo(() => {
    return parcelState.parcelNumber.trim() && parcelCountyAutocomplete.selectedCounty;
  }, [parcelState.parcelNumber, parcelCountyAutocomplete.selectedCounty]);

  return (
    <div>
      <div className="frm-field-2">
        <div className="form-group input-sticky">
          <FloatingInput
            label="Parcel Number"
            value={parcelState.parcelNumber}
            onChange={handleParcelNumberChange}
            placeholder="Enter parcel number"
          />
        </div>
        <div className="form-group input-sticky relative droparrow">
          <FloatingInput
            type="search"
            label="County"
            placeholder="Enter county name"
            value={parcelCountyAutocomplete.query}
            onChange={parcelCountyAutocomplete.onChange}
            onBlur={parcelCountyAutocomplete.onBlur}
            onKeyDown={parcelCountyAutocomplete.onKeyDown}
          />
          {/* County suggestions dropdown */}
          {parcelCountyAutocomplete.suggestions.length > 0 && (
            <div className="search-list-prop county-suggestions">
              {parcelCountyAutocomplete.suggestions.map((s, idx) => (
                <div
                  key={s.code}
                  ref={(el) => (parcelCountyAutocomplete.suggestionItemRefs.current[idx] = el)}
                  className={`search-list-prop-wrap ${idx === parcelCountyAutocomplete.activeIndex ? "active" : ""}`}
                  onMouseEnter={() => parcelCountyAutocomplete.setActiveIndex(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    parcelCountyAutocomplete.selectSuggestion(s);
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    parcelCountyAutocomplete.selectSuggestion(s);
                  }}
                >
                  <div className="search-list-prop-heading">
                    {s.displayText}
                  </div>
                  <div className="search-list-prop-desc">
                    {s.code}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {shouldShowSubmitButton && (
        <div className="frm-field-1 slide-down">
          <div className="form-group text-right">
            <button
              type="button"
              className="submit-btn btn-bg inpt-btn sht-btn ch-btnsave-new"
              onClick={handleParcelSearch}
              disabled={parcelState.isSearching}
            >
              {parcelState.isSearching ? "Searching..." : "Search Parcel"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

ParcelTab.displayName = "ParcelTab";

export default ParcelTab;